import { Component } from '@angular/core';
import { ModalController } from '@ionic/angular'
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';;
import { Router, ActivatedRoute } from '@angular/router';
import * as moment from 'moment';

import { Certification } from 'src/providers/certification/certification';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Cleanings } from 'src/providers/cleanings/cleanings';
import { ClaimJobs } from 'src/providers/claim-jobs/claim-jobs';
import { Events } from 'src/providers/events/events';
import { Homekeepers } from 'src/providers/homekeepers/homekeepers';
import { Me } from 'src/shared/providers/me';
import { Team } from 'src/providers/team/team';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { AppConfig } from 'src/shared/providers/config';

import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm';
import { SuccessPageParamsModel } from 'src/pages/success/success';

import { CertificationsModel } from 'src/shared/models/certification.model';
import { ParsedExampleCoopModel, ExampleCoopModel } from 'src/shared/models/example-coop.model';
import { forkJoin, interval, Subscription } from 'rxjs';
import promiseUtils from 'src/shared/utils/promise-utils';
import { getDayInOwnOffset, getLocalDay } from 'src/shared/utils/date-utils';
import { TimeoutErrorHandler } from 'src/shared/providers/http/timeout-error-handler';
import { AppState } from 'src/shared/providers/http/app-state';
import { Timeout } from 'src/shared/components/timeout/timeout';
import { takeWhile } from 'rxjs/operators';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { TidyFirstNamePipe } from 'src/shared/pipes/tidy-first-name';
import { PrivateClient } from 'src/providers/private-client/private-client';
import { DateTime as LuxonDateTime } from 'luxon';
import { TimePipe } from 'src/shared/pipes/tidy-time';
import { CommonUtils } from 'src/shared/utils/common-utils';

declare const mapboxgl: any;

@Component({
    selector: 'add',
    templateUrl: 'add.html',
    styleUrls: ['./add.scss'],
    standalone: false
})
export class AddPage extends Timeout {

  addedZipcode: boolean;
  customBack: false;
  apiError: string;
  didGoLive: boolean;
  clientRequests: any;
  currentWeek: any;
  date: Date;
  delegationRequests: any;
  errorMessage: string;
  hasNoExampleCoops: boolean;
  homeLongitude: number;
  homeLatitude: number;
  isTimeOut = false;
  isLimitedAccount: boolean;
  jobMaps = [];
  legends: any;
  lms: any;
  lmsData: any;
  map: any = {};
  noItemsAvailable: boolean;
  examples: ParsedExampleCoopModel[];
  examplesLength: number;
  resumeSubscription;
  today: string;
  hasTeamMembers: boolean;
  privateClients: any[] = [];
  page: number = 1;
  perPage: number = 100;
  emergencyCleaningsResponse: any[] = [];
  totalEmergencyCleanings: number = 0;
  fetchInterval$: Subscription;
  customBackRoute: string;
  dateRangeForm: UntypedFormGroup;
  selectOptions: any;
  startDate: string;
  endDate: string;
  rangeOption: string = 'all_time';
  isLoadingMoreJobs: boolean = false;

  constructor(
    private certification: Certification,
    private cleanings: Cleanings,
    private customNavCtrl: CustomNavController,
    private claimJobs: ClaimJobs,
    private events: Events,
    private homekeepers: Homekeepers,
    public me: Me,
    private modalController: ModalController,
    router: Router,
    private storage: TidyStorage,
    private team: Team,
    route: ActivatedRoute,
    timeoutErrorHandler: TimeoutErrorHandler,
    private appState: AppState,
    private privateClient: PrivateClient,
    private fb: FormBuilder,
    private utils: CommonUtils
  ) {
    super(timeoutErrorHandler, router, route);
    this.dateRangeForm = fb.group({
      start: '',
      end: '',
      customStringSelect: ''
    });
    this.selectOptions = [
      { viewValue: 'Custom', value: 'custom' },
      { viewValue: 'Today', value: 'today' },
      { viewValue: 'Next Week', value: 'next_week' },
      { viewValue: 'Next Month', value: 'next_month' },
      { viewValue: 'All', value: 'all_time' }
    ];
  }

  async ionViewDidEnter() {
    this.loaded = false;
    this.page = 1;
    const navigationState = history.state;
    this.customBackRoute = navigationState?.customBackRoute;
    const startDate = localStorage.getItem('jobRequestsStartDate');
    if (startDate) {
      this.startDate = startDate;
      this.endDate = localStorage.getItem('jobRequestsEndDate');
      this.rangeOption = localStorage.getItem('jobRequestsStringValue');
      this.dateRangeForm.patchValue({
        customStringSelect: this.rangeOption,
        start: this.startDate ? this.createLocalDate(this.startDate) : null,
        end: this.endDate ? this.createLocalDate(this.endDate) : null
      });
    } else {
      this.rangeOption = 'all_time';
      this.dateRangeForm.patchValue({
        customStringSelect: 'all_time'
      });
    }
    this.alreadyShowedOfflineOrTimeoutAlert = false;
    if (!await this.appState.isOnline()) {
      this.alreadyShowedOfflineOrTimeoutAlert = true;
      return this.timeoutErrorHandler.showOfflineAlert(this.successPageLoad, 'dashboard', this.retrySubject);
    }
    await this.getInitialData();
    try {
      this.privateClients = await this.privateClient.getPrivateClients();
    } catch (err) {
      this.showMessage(err);
    }
    this.loaded = true;
    if (!this.didGoLive && this.currentWeek?.days.length === 0 && this.currentWeek?.jobs.length === 0) {
      const teamServiceTypes = await this.team.getTeamServiceTypes();
      let offersCleaningServices = false;
      teamServiceTypes.forEach((service) => {
        if (service.name === 'Regular Cleaning') {
          offersCleaningServices = true;
        }
      });
      if (offersCleaningServices) {
        try {
          const examples: ExampleCoopModel[] = await this.certification.getPreCertificationCoops();
          this.examplesLength = examples.length;
          this.examples = this.parseExamples(examples);
          this.hasNoExampleCoops = this.examplesLength === 0;
        } catch (err) {
          this.hasNoExampleCoops = true;
        }
      } else {
        this.hasNoExampleCoops = true;
      }
    }
    this.events.subscribe('coop-cleaning:claimed', () => this.modalController.dismiss());
    this.successPageLoad.emit(true);
    this.getClientRequests()
    this.fetchDelegations()
    this.getHkHomeData();
    /*this.fetchInterval$ = interval(60000).subscribe(() => {
      this.refreshEmergencyCleanings();
      this.getClientRequests()
      this.fetchDelegations()
    });*/
  }

  async getInitialData(): Promise<void> {
    try {
      this.isLoadingMoreJobs = false; // Reset loading state on initial load
      this.isLimitedAccount = await this.storage.retrieve('isLimitedAccount');
      const [emergencyCleanings, certification] = await Promise.all([
        this.homekeepers.fetchLmsNoCache(this.page, this.perPage, this.startDate, this.endDate),
        this.certification.getCertificationsNoCache(),
      ]);
      this.totalEmergencyCleanings = emergencyCleanings.headers.get('x-total-records');
      this.emergencyCleaningsResponse = emergencyCleanings.body;
      this.handleEmergencyCleanings(emergencyCleanings.body);
      this.handleCertification(certification);
      this.today = moment().format('YYYY-MM-DD');
      Promise.resolve();
    } catch(err) {
      this.showMessage(err)
      Promise.reject(err);
    }
  }

  async refreshEmergencyCleanings(): Promise<void> {
    try {
      this.page = 1;
      const emergencyCleanings = await this.homekeepers.fetchLmsNoCache(this.page, this.perPage, this.startDate, this.endDate);
      this.totalEmergencyCleanings = emergencyCleanings.headers.get('x-total-records');
      this.emergencyCleaningsResponse = emergencyCleanings.body;
      this.handleEmergencyCleanings(emergencyCleanings.body);
    } catch(err) {
      this.showMessage(err)
    }
  }

  async loadMoreEmergencyCleanings(event) {
    const currentWeekDaysLength = this.currentWeek?.days?.length;
    setTimeout(async () => {
      this.page += 1;
      let emergencyCleanings = await this.homekeepers.fetchLmsNoCache(this.page, this.perPage, this.startDate, this.endDate);
      this.emergencyCleaningsResponse = [...this.emergencyCleaningsResponse, ...emergencyCleanings.body];
      await this.handleEmergencyCleanings(this.emergencyCleaningsResponse, true);
      if (emergencyCleanings.body.length !== 0 && currentWeekDaysLength == this.currentWeek?.days?.length && this.emergencyCleaningsResponse.length < this.totalEmergencyCleanings) {
        // Show loading state when retriggering
        this.isLoadingMoreJobs = true;
        this.loadMoreEmergencyCleanings(event);
      } else {
        // Hide loading state when new content is added
        this.isLoadingMoreJobs = false;
      }
      event.target.complete();
      if (this.emergencyCleaningsResponse.length >= this.totalEmergencyCleanings) {
        event.target.disabled = true;
        this.isLoadingMoreJobs = false;
      }
    }, 500);
  }

  checkShouldShowBigSpender(cleaning) {
    if (!this.privateClients || this.privateClients.length === 0) {
      return false;
    }
    const isCleaningFromPrivateClient = this.privateClients.some(client => client?.id === cleaning?.customer_id);
    return cleaning?.customer_is_premium && !isCleaningFromPrivateClient;
  }

  handleCertification(certification: CertificationsModel) {
    this.addedZipcode = certification.steps.profile == 'complete';
    this.didGoLive = certification.steps.go_live === 'complete';
  }

  async fetchDelegations() {
    try {
      const delegationRequests = await this.cleanings.fetchDelegations();
      if (this.startDate && this.endDate) {
        const startDateFormatted = getDayInOwnOffset(this.startDate);
        const endDateFormatted = getDayInOwnOffset(this.endDate);
        this.delegationRequests = delegationRequests.filter(request => {
          try {
            const formattedDate = LuxonDateTime.fromFormat(request.job_data.friendly_date, 'MM/dd/yyyy').toFormat('yyyy-MM-dd');
            return formattedDate >= startDateFormatted && formattedDate <= endDateFormatted;
          } catch (err) {
            return true;
          }
        });
      } else {
        this.delegationRequests = delegationRequests;
      }
    } catch (err) {
      this.showMessage(err)
    }
  }

  async fetchCleaning(date: string): Promise<any> {
    const activeStandbyCleanings = await this.cleanings.getActiveStandbyCleanings(date);
    return activeStandbyCleanings.map((item) => {
      return {
        date: item.date,
        startTime: moment(item.start_time, 'HH:mm').format('h:mma'),
        latitude: item.address.latitude,
        longitude: item.address.longitude
      }
    });
  }

  checkIfHasGenderSpecificJobs() {
    let hasGenderSpecificJobs = false;
    this.currentWeek.days.map((day) => {
      day.cleanings.map((cleaning) => {
        if (cleaning?.homekeeper_preferences?.gender == 'female' ||  cleaning?.homekeeper_preferences?.gender == 'male') {
          hasGenderSpecificJobs = true;
        }
      });
    });
    return hasGenderSpecificJobs;
  }

  async getHkHomeData(): Promise<Array<number>> {
    const hk = await this.me.load();
    this.homeLongitude = hk.user.longitude;
    this.homeLatitude = hk.user.latitude;
    return [this.homeLongitude, this.homeLatitude];
  }

  async awaitMapElement(mapId, atempts = 1) {
    const mapElement = document.getElementById(mapId);

    if(!mapElement && atempts < 4) {
      await promiseUtils.sleep(300);
      await this.awaitMapElement(mapId, (atempts + 1));
    }
  }

  getCenterCoordinates(cards: any[], hasHome: boolean) {
    let centerLongitude: number;
    let centerLatitude: number;

    if (hasHome) {
      centerLongitude = this.homeLongitude;
      centerLatitude = this.homeLatitude;
    } else {
      let totalLongitude = 0;
      let totalLatitude = 0;
      cards.forEach((cleaning) => {
        totalLongitude += cleaning.address?.longitude;
        totalLatitude += cleaning.address?.latitude;
      });
      centerLongitude = totalLongitude / cards.length;
      centerLatitude = totalLatitude / cards.length;
    }
    return { centerLatitude, centerLongitude };
  }

  async setMap(item, index) {
    try {
      const scheduledCleanings = await this.fetchCleaning(item.formatedDate);
      this.legends = this.getDayLegend(scheduledCleanings);
      if(item?.showMap != undefined && item.showMap === false){
        this.currentWeek.days[index].showMap = true;
        return;
      }

      const hasHome = this.homeLongitude !== null && this.homeLongitude !== undefined;
      const { centerLatitude, centerLongitude } = this.getCenterCoordinates(item.cards, hasHome);

      mapboxgl.accessToken = AppConfig.MAPBOX_KEY;
      const mapElementId = `map${index}`;
      await this.awaitMapElement(mapElementId);

      const map = new mapboxgl.Map({
        container: mapElementId,
        style: 'mapbox://styles/mapbox/dark-v10',
        center: [ centerLongitude, centerLatitude ],
        zoom: 3,
        maxZoom: 11,
        minZoom: 3
      });
      let bounds = new mapboxgl.LngLatBounds();
      scheduledCleanings.forEach((cleaning, i) => {
        if (cleaning.date === item.formatedDate) {
          this.addScheduledMarker(cleaning, map, i);
        }
      });
      item.cleanings.forEach( (cleaning, i) => {
        this.addOppMarker(cleaning, map, i);
        bounds.extend([cleaning.longitude, cleaning.latitude]);
      });
      if (hasHome) {
        this.addHomeMarker(map);
        bounds.extend([this.homeLongitude, this.homeLatitude]);
      }
      map.fitBounds(bounds, {
        padding: 20,
        maxZoom: 11,
        animate: false
      });
      this.currentWeek.days[index].showMap = true;
    }
    catch (err) {
      this.showMessage(err)
    }
  }

  hideExampleMap(index){
    this.examples[index].showMap = false;
  }

  getDayLegend(scheduledCleanings: any[]) {
    const legends = this.currentWeek.days.map(day => {
      const times = scheduledCleanings.map((cleaning) => {
        if (day.formatedDate === cleaning.date) {
          return cleaning.startTime;
        }
      });
      return times.filter((time) => time);
    });
    return legends;
  }

  addScheduledMarker(cleaning, map, i) {
    const el = document.createElement('div');
    el.className = 'marker';
    el.innerHTML = `<span class="map-marker-date">${cleaning.startTime}</span>`;
    this.createMarkerInstance({map, el, longLat: [ cleaning.longitude, cleaning.latitude ]});
  }

  async addHomeMarker(map) {
    const el = document.createElement('div');
    el.className = 'marker';
    el.innerHTML = `<image src="assets/img/home-white.svg" style="height: 30px"></image>`;
    this.createMarkerInstance({map, el, longLat: [ this.homeLongitude, this.homeLatitude ]});
  }

  addOppMarker(cleaning, map, i) {
    const el = document.createElement('div');
    const letter = String.fromCharCode(65+i);
    el.className = 'marker';
    el.innerHTML = `<span class="map-marker" style="margin-top: ${i * 2}0px">${letter}</span>`;
    this.createMarkerInstance({map, el, longLat: [ cleaning.longitude, cleaning.latitude ]})
  }

  createMarkerInstance(obj){
    const {el, longLat, map} = obj;
    new mapboxgl.Marker(el)
      .setLngLat(longLat)
      .addTo(map);
  }

  async handleEmergencyCleanings(cleanings: any[], isLoadingMoreJobs = false): Promise<void> {
    try {
      this.noItemsAvailable = (cleanings.length === 0);
      const data = await this.claimJobs.parseDays(cleanings);
      if (isLoadingMoreJobs) {
        //Avoid replacing the entire currentWeek variable to avoid sending user to the top of the page
        data.currentWeek.days.map((day) => {
          let isDayAlreadyAdded = false;
          this.currentWeek.days.map((currentDay) => {
            if (day.dateLabel === currentDay.dateLabel) {
              isDayAlreadyAdded = true;
              currentDay.cleanings = [...currentDay.cleanings, ...day.cleanings.filter(newCleaning =>
                !currentDay.cleanings.some(existingCleaning =>
                  existingCleaning.time === newCleaning.time && existingCleaning.job_id === newCleaning.job_id
                )
              )];
            }
          });
          if (!isDayAlreadyAdded) {
            this.currentWeek.days.push(day);
          }
        });
        this.currentWeek.jobs = data.currentWeek.jobs;
      } else {
        this.currentWeek = data.currentWeek;
        this.noItemsAvailable = data.noItemsAvailable;
      }
      const hasGenderSpecificJobs = this.checkIfHasGenderSpecificJobs();
      if (hasGenderSpecificJobs) {
        this.hasTeamMembers = await this.team.getIfHasTeamMembers();
      }
    } catch (err) {
      this.isTimeOut = (err.name === 'TimeoutError');
      this.showMessage(err)
    }
  }

  async getClientRequests() {
    try {
      const clientRequests = await this.homekeepers.getClientRequests();
      let filteredClientRequests = clientRequests;
      if (this.startDate && this.endDate) {
        const startDateFormatted = getDayInOwnOffset(this.startDate);
        const endDateFormatted = getDayInOwnOffset(this.endDate);
        filteredClientRequests = clientRequests.filter(request => {
          return request.request_times.some(time => {
            try {
              const timeDate = LuxonDateTime.fromISO(time.date).toFormat('yyyy-MM-dd');
              return timeDate >= startDateFormatted && timeDate <= endDateFormatted;
            } catch (err) {
              return true;
            }
          });
        });
      }
      this.clientRequests = await Promise.all(filteredClientRequests.map(async (item) => {
        item.request_times = item.request_times.reduce((accumulator, currentItem) => {
          accumulator[currentItem.date] = [...accumulator[currentItem.date] || [], currentItem];
          return accumulator;
        }, {});
        item.request_times = Object.keys(item.request_times).map( (i) => item.request_times[i]);
        item['bookingFormAnswers'] = await this.homekeepers.getProRequestBookingFormAnswers(item.id);
        return item;
      }));
    } catch (err) {
      this.showMessage(err)
    }
  }

  parseExamples(examples: ExampleCoopModel[]): ParsedExampleCoopModel[] {
    const days = [];
    const today = LuxonDateTime.now();
    const todayNumber = today.weekday;
    for (let i=0; i<7; i++) {
      const day = today.plus({ days: i });
      let accNumber = todayNumber + i;
      if (accNumber > 7) {
        accNumber -= 7;
      }
      const parsedExamples = [];
      examples.map((example) => {
        if (example.day_of_week === accNumber) {
          parsedExamples.push(example);
        };
      });
      parsedExamples.sort((a,b) => {
        return parseInt(a.time, 10) - parseInt(b.time, 10);
      });
      days.push({
        day: day.toISO(),
        examples: parsedExamples
      });
    }
    setTimeout(() => {
      this.setExamplesMap(days[0], 0);
    }, 100);
    return days;
  }

  async setExamplesMap(day, index) {
    if (day?.showMap != undefined && day.showMap === false) {
      this.examples[index].showMap = true;
      return;
    }
    const hkHomeCoords = await this.getHkHomeData();
    mapboxgl.accessToken = AppConfig.MAPBOX_KEY;
    const map = new mapboxgl.Map({
      container: `map${index}`,
      style: 'mapbox://styles/mapbox/dark-v10',
      center: hkHomeCoords,
      zoom: 5,
      maxZoom: 11,
      minZoom: 5
    });
    day.examples.forEach( (example, i) => {
      this.addExampleOppMarker(example, map, i);
    });
    if (this.homeLongitude !== null && this.homeLongitude !== undefined) {
      this.addHomeMarker(map);
    }
    this.examples[index].showMap = true;
  }

  addExampleOppMarker(example, map, i) {
    const el = document.createElement('div');
    const letter = String.fromCharCode(65+i);
    el.className = 'marker';
    el.innerHTML = `<span class="map-marker" style="margin-top: ${i * 2}0px">${letter}</span>`;
    this.createMarkerInstance({map, el, longLat: [ example.avg_longitude, example.avg_latitude ]})
  }

  async showExampleRequestAlert() {
    const successParams: SuccessPageParamsModel = {
      header: 'Finish Getting Started to Use This',
      body: 'Want to get job requests?  Some clients require certifications.  It\'s quick, easy and free!',
      buttonText: 'View Getting Started Steps in the Dashboard',
      buttonRoute: `dashboard`
    };
    this.customNavCtrl.navigateForward('success', successParams);
  }

  navigateToCleaning(cleaning) {
    const params = {
      cleaning,
      homeLongitude: this.homeLongitude,
      homeLatitude: this.homeLatitude,
      jobsFromSameClient: this.claimJobs.getJobsFromSameClient(this.currentWeek, cleaning),
      jobsGroupedByJobId: this.currentWeek.jobs,
      hasTeamMembers: this.hasTeamMembers
    }
    this.customNavCtrl.navigateForward('claim-job', params);
  }

  navigateToAcceptRequest(request) {
    this.customNavCtrl.navigateForward('client-request', request);
  }

  navigateToProposeAlternateRequest(request) {
    this.customNavCtrl.navigateForward('client-request-counter', request);
  }

  ionViewWillLeave(): void {
    this.loaded = true;
    if (this.resumeSubscription) {
      this.resumeSubscription.unsubscribe();
    }
    if (this.fetchInterval$) {
      this.fetchInterval$.unsubscribe();
    }
  }

  async addAccountInfo() {
    const wasInvitedByClient = localStorage.getItem('wasInvitedByClient') == 'true';
    if (wasInvitedByClient) {
      localStorage.setItem('cameFromWasInvitedByClientOnboarding', 'true');
    } else {
      localStorage.setItem('cameFromWasInvitedByClientOnboarding', 'false');
    }
    this.customNavCtrl.navigateForward('account-info', {finishPage: 'add'});
  }

  async declineRequest(request) {
    const params: ConfirmPageParamsModel = {
      title: 'Decline Request?',
      body: `${new TranslationPipe().transform('Notify')} ${request.address.customer.first_name} ${new TranslationPipe().transform('that you cannot help them at those times, but they are free to book you on your current bookable schedule.')}`,
      backText: 'Go Back',
      confirmText: 'Decline Request',
      confirmAction: this.confirmDeclineRequest.bind(this),
      confirmActionParams: request.id,
    };
    const confirmationModal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false
    });
    confirmationModal.present();
  }

  async confirmDeclineRequest(id) {
    await this.homekeepers.declineClientRequest(id);
    const successParams: SuccessPageParamsModel = {
      header: 'Request Declined',
      body: 'We have sent a notification to this Client.',
      buttonText: 'Ok',
      buttonRoute: `add`
    };
    this.modalController.dismiss();
    this.customNavCtrl.navigateForward('success', successParams);
  }

  async cancelCounterRequest(request) {
    const params: ConfirmPageParamsModel = {
      title: 'Cancel Request?',
      body: `${new TranslationPipe().transform('Notify')} ${request.address.customer.first_name} ${new TranslationPipe().transform('that you cannot help them at those times, but they are free to book you on your current bookable schedule.')}`,
      backText: 'Go Back',
      confirmText: 'Cancel Request',
      confirmAction: this.confirmCancelCounterRequest.bind(this),
      confirmActionParams: request.id,
    };
    const confirmationModal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false
    });
    confirmationModal.present();
  }

  async confirmCancelCounterRequest(id) {
    await this.homekeepers.cancelCounterRequest(id);
    const successParams: SuccessPageParamsModel = {
      header: 'Request Cancelled',
      body: 'We have sent a notification to this Client.',
      buttonText: 'Ok',
      buttonRoute: `add`
    };
    this.modalController.dismiss();
    this.customNavCtrl.navigateForward('success', successParams);
  }

  goToNewClients() {
    this.customNavCtrl.navigateForward('new-clients');
  }

  goToDriveTime() {
    this.customNavCtrl.navigateForward('profile/location');
  }

  goToRates() {
    this.customNavCtrl.navigateForward('services');
  }

  parseServiceLength(name) {
    const lastIndex = name.lastIndexOf(" ");
    return name.substring(0, lastIndex);
  }

  showMessage(err) {
    this.errorMessage = err?.error?.message ?? err?.message;
    this.utils.showError(this.errorMessage);
  }

  goToDelegationRequest(delegation) {
    const params = {
      updateId: delegation.job_data.homekeeper_update_id,
      requestId: delegation.id,
      delegatorName: new TidyFirstNamePipe().transform(delegation.delegated_by_team_member.homekeeper.name),
      jobDate: delegation.job_data.friendly_date,
      jobTime: delegation.job_data.friendly_start_time,
      jobDuration: delegation.job_data.duration_in_hours,
      cameFromAddPage: true
    };
    this.customNavCtrl.navigateForward('accept-reject-job', params);
  }

  async selectCustomDateRange(event: any) {
    this.loaded = false;
    const timezone = await this.storage.retrieve('timezone');
    // Create dates in the target timezone to preserve the intended date
    this.startDate = LuxonDateTime.fromFormat(
      getLocalDay(event.start),
      'yyyy-MM-dd',
      { zone: timezone }
    ).startOf('day').toISO();
    this.endDate = LuxonDateTime.fromFormat(
      getLocalDay(event.end),
      'yyyy-MM-dd',
      { zone: timezone }
    ).endOf('day').toISO();
    localStorage.setItem('jobRequestsStringValue', 'custom');
    localStorage.setItem('jobRequestsStartDate', this.startDate);
    localStorage.setItem('jobRequestsEndDate', this.endDate);
    await Promise.all([
      this.refreshEmergencyCleanings(),
      this.getClientRequests(),
      this.fetchDelegations()
    ]);
    this.loaded = true;
  }

  getAllQuotesForJob(cleaning: any): any {
    if (!cleaning?.job_id || !this.currentWeek?.days) {
      return cleaning?.vendor_outreach_request_quote || null;
    }

    // Find all cleanings with the same job_id across all days
    const allCleaningsForJob: any[] = [];
    this.currentWeek.days.forEach(day => {
      if (day.filteredCleanings) {
        day.filteredCleanings.forEach(c => {
          if (c.job_id === cleaning.job_id && c.vendor_outreach_request_quote) {
            allCleaningsForJob.push(c);
          }
        });
      }
      // Also check cleanings array if it exists
      if (day.cleanings) {
        day.cleanings.forEach(c => {
          if (c.job_id === cleaning.job_id && c.vendor_outreach_request_quote &&
              !allCleaningsForJob.some(existing => existing.emergency_cleaning_request_id === c.emergency_cleaning_request_id)) {
            allCleaningsForJob.push(c);
          }
        });
      }
    });

    if (allCleaningsForJob.length === 0) {
      return cleaning?.vendor_outreach_request_quote || null;
    }

    // Aggregate quotes - collect all times and dates, use first quote's other data
    const firstQuote = allCleaningsForJob[0].vendor_outreach_request_quote;
    const allTimes = allCleaningsForJob
      .map(c => c.vendor_outreach_request_quote?.time)
      .filter(time => time)
      .sort()
      .join(', ');

    // Collect all unique dates
    const allDates = allCleaningsForJob
      .map(c => c.vendor_outreach_request_quote?.date)
      .filter(date => date)
      .filter((date, index, self) => self.indexOf(date) === index) // Get unique dates
      .sort((a, b) => moment(a).diff(moment(b))); // Sort chronologically

    return {
      id: firstQuote.id,
      amount: firstQuote.amount,
      date: firstQuote.date,
      dates: allDates, // Array of all unique dates
      time: allTimes, // Concatenated times
      times: allTimes.split(', '), // Array of times for display
      homekeeper_notes: firstQuote.homekeeper_notes,
      status: firstQuote.status,
      count: allCleaningsForJob.length // Number of quotes
    };
  }

  shouldShowQuoteForCleaning(cleaning: any, day: any, index: number): boolean {
    // Only show quote info for the first cleaning of each job_id in the filteredCleanings
    if (!cleaning?.job_id || !day?.filteredCleanings) {
      return !!cleaning?.vendor_outreach_request_quote;
    }

    // Check if this is the first occurrence of this job_id in filteredCleanings
    const firstIndex = day.filteredCleanings.findIndex(c => c.job_id === cleaning.job_id);
    return firstIndex === index && !!this.getAllQuotesForJob(cleaning);
  }

  formatQuoteTimes(quote: any): string {
    if (!quote?.times || quote.times.length === 0) {
      return '';
    }
    const timePipe = new TimePipe();
    return quote.times.map(time => timePipe.transform(time)).join(', ');
  }

  formatQuoteIndicator(quote: any): string {
    if (!quote) {
      return '';
    }

    // Format amount using the same logic as tcurrency pipe
    let formattedAmount = '$0';
    if (quote.amount) {
      const normalizedValue = Math.sqrt(quote.amount * quote.amount);
      const valueWithDecimal = normalizedValue / 100;
      const strValue = quote.amount % 100 > 0 ? valueWithDecimal.toFixed(2) : valueWithDecimal.toString();
      const str = strValue.toString().split('.');
      str[0] = str[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const strValueWithCommas = str.join('.');
      formattedAmount = quote.amount < 0 ? '-$' + strValueWithCommas : '$' + strValueWithCommas;
    }

    let datesText = '';
    if (quote.dates && quote.dates.length > 0) {
      datesText = quote.dates
        .map(date => moment(date).format('MMM D'))
        .join(', ');
    } else if (quote.date) {
      datesText = moment(quote.date).format('MMM D');
    }

    if (datesText) {
      return `Quote Sent: ${formattedAmount} for ${datesText}`;
    } else {
      return `Quote Sent: ${formattedAmount}`;
    }
  }

  async selectStringDateRange(event: any) {
    this.loaded = false;
    const timezone = await this.storage.retrieve('timezone');
    if (event === 'all_time') {
      localStorage.setItem('jobRequestsStringValue', 'all_time');
      localStorage.removeItem('jobRequestsStartDate');
      localStorage.removeItem('jobRequestsEndDate');
      this.startDate = '';
      this.endDate = '';
    }
    if (event === 'today') {
      localStorage.setItem('jobRequestsStringValue', 'today');
      this.startDate = LuxonDateTime.now().setZone(timezone).startOf('day').toISO();
      this.endDate = LuxonDateTime.now().setZone(timezone).endOf('day').toISO();
    }
    if (event === 'next_week') {
      localStorage.setItem('jobRequestsStringValue', 'next_week');
      this.startDate = LuxonDateTime.now().setZone(timezone).startOf('day').toISO();
      this.endDate = LuxonDateTime.now().setZone(timezone).plus({ weeks: 1 }).endOf('day').toISO();
    }
    if (event === 'next_month') {
      localStorage.setItem('jobRequestsStringValue', 'next_month');
      this.startDate = LuxonDateTime.now().setZone(timezone).startOf('day').toISO();
      this.endDate = LuxonDateTime.now().setZone(timezone).plus({ months: 1 }).endOf('day').toISO();
    }
    localStorage.setItem('jobRequestsStartDate', this.startDate);
    localStorage.setItem('jobRequestsEndDate', this.endDate);
    await Promise.all([
      this.refreshEmergencyCleanings(),
      this.getClientRequests(),
      this.fetchDelegations()
    ]);
    this.loaded = true;
  }

  private createLocalDate(isoString: string): Date {
    // Extract just the date part (YYYY-MM-DD) from the ISO string
    const dateMatch = isoString.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      const datePart = dateMatch[1];
      const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
      // Create a local Date object for this specific date (month is 0-indexed)
      return new Date(year, month - 1, day);
    }
    // Fallback to regular Date creation if parsing fails
    return new Date(isoString);
  }

}
